import React, { useCallback, useMemo } from "react";
import { BudgetType } from "@akfdev-com/shared/src/models";
import CategoryPicker from "./CategoryPicker";
import { Button } from "../ui";

/** Local interface for line data used in the editor */
interface BudgetLineInput {
  id: string;
  categoryId: string;
  categoryName: string;
  budgetAmount: number; // in pence
  notes?: string;
}

interface BudgetLineEditorProps {
  /** Array of budget lines */
  lines: BudgetLineInput[];
  /** Callback when lines are modified */
  onChange: (lines: BudgetLineInput[]) => void;
  /** Budget type to filter categories */
  budgetType: BudgetType;
  /** Whether the editor is disabled */
  disabled?: boolean;
  /** Whether the budget is in a state where editing is allowed */
  readOnly?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Error message to display */
  error?: string;
}

/**
 * Budget Line Editor component.
 *
 * Allows adding, editing, and removing budget line items.
 * Each line consists of a category (filtered by budget type) and an amount.
 * Amounts are stored as integer pence internally; users input in pounds.
 *
 * - Add new lines with category and amount
 * - Edit existing lines inline
 * - Remove lines with confirmation
 * - Display running total
 *
 * **Validates: Requirements 2.1, 2.2, 2.3, 3.4**
 */
const BudgetLineEditor: React.FC<BudgetLineEditorProps> = ({
  lines,
  onChange,
  budgetType,
  disabled = false,
  readOnly = false,
  className = "",
  error,
}) => {
  /** Generate a unique ID for new lines */
  const generateLineId = useCallback(() => {
    return `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  /** Handle adding a new line */
  const handleAddLine = useCallback(() => {
    const newLine: BudgetLineInput = {
      id: generateLineId(),
      categoryId: "",
      categoryName: "",
      budgetAmount: 0,
      notes: "",
    };
    onChange([...lines, newLine]);
  }, [lines, onChange, generateLineId]);

  /** Handle updating a line's category */
  const handleCategoryChange = useCallback(
    (lineId: string, categoryId: string, categoryName: string) => {
      const updatedLines = lines.map((line) =>
        line.id === lineId ? { ...line, categoryId, categoryName } : line,
      );
      onChange(updatedLines);
    },
    [lines, onChange],
  );

  /** Handle updating a line's amount (input in pounds, stored as pence) */
  const handleAmountChange = useCallback(
    (lineId: string, amountInPounds: string) => {
      // Parse the input, handling empty strings and decimals
      const parsed = parseFloat(amountInPounds);
      if (isNaN(parsed)) {
        // If empty or invalid, store as 0
        const updatedLines = lines.map((line) =>
          line.id === lineId ? { ...line, budgetAmount: 0 } : line,
        );
        onChange(updatedLines);
        return;
      }
      // Multiply by 100 to convert to pence, round to handle floating point
      const amountInPence = Math.round(parsed * 100);
      const updatedLines = lines.map((line) =>
        line.id === lineId ? { ...line, budgetAmount: amountInPence } : line,
      );
      onChange(updatedLines);
    },
    [lines, onChange],
  );

  /** Handle removing a line */
  const handleRemoveLine = useCallback(
    (lineId: string) => {
      const updatedLines = lines.filter((line) => line.id !== lineId);
      onChange(updatedLines);
    },
    [lines, onChange],
  );

  /** Calculate the total budget amount in pence */
  const totalInPence = useMemo(() => {
    return lines.reduce((sum, line) => sum + (line.budgetAmount || 0), 0);
  }, [lines]);

  /** Format pence to display string */
  const formatPence = (pence: number): string => {
    return (pence / 100).toFixed(2);
  };

  /** Check if a line is complete (has category and amount) */
  const isLineComplete = (line: BudgetLineInput): boolean => {
    return !!line.categoryId && line.budgetAmount > 0;
  };

  /** Check if any line is incomplete */
  const hasIncompleteLines = useMemo(() => {
    return lines.some((line) => !isLineComplete(line));
  }, [lines]);

  const isDisabled = disabled || readOnly;

  return (
    <div className={className}>
      {/* Section header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-700">
            Budget Lines
          </span>
          <span className="text-red-500 ml-1">*</span>
        </div>
        {!isDisabled && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleAddLine}
            disabled={disabled}
          >
            <svg
              className="w-4 h-4 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Add Line
          </Button>
        )}
      </div>

      {/* Error message */}
      {error && (
        <p className="mb-3 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}

      {/* Empty state */}
      {lines.length === 0 && (
        <div className="text-center py-6 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-500 mb-3">
            No budget lines yet. Add at least one line to create a budget.
          </p>
          {!isDisabled && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleAddLine}
            >
              Add First Line
            </Button>
          )}
        </div>
      )}

      {/* Lines list */}
      {lines.length > 0 && (
        <div className="space-y-3">
          {lines.map((line, index) => (
            <div
              key={line.id}
              className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
            >
              {/* Line number */}
              <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-xs font-medium text-gray-500 bg-white rounded-full border border-gray-300">
                {index + 1}
              </span>

              {/* Category picker */}
              <div className="flex-1 min-w-0">
                <CategoryPicker
                  budgetType={budgetType}
                  value={line.categoryId}
                  onChange={(categoryId, categoryName) =>
                    handleCategoryChange(line.id, categoryId, categoryName)
                  }
                  disabled={isDisabled}
                  placeholder="Select category"
                />
              </div>

              {/* Amount input */}
              <div className="flex-shrink-0 w-32">
                <label
                  htmlFor={`amount-${line.id}`}
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Amount (£)
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                    £
                  </span>
                  <input
                    type="number"
                    id={`amount-${line.id}`}
                    value={formatPence(line.budgetAmount)}
                    onChange={(e) =>
                      handleAmountChange(line.id, e.target.value)
                    }
                    disabled={isDisabled}
                    step="0.01"
                    min="0"
                    className={[
                      "block w-full pl-7 pr-3 py-2",
                      "rounded-md border-gray-300 shadow-sm",
                      "focus:border-primary-500 focus:ring-primary-500 sm:text-sm",
                      "disabled:bg-gray-100 disabled:cursor-not-allowed",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    placeholder="0.00"
                  />
                </div>
              </div>

              {/* Remove button */}
              {!isDisabled && (
                <div className="flex-shrink-0 pt-6">
                  <button
                    type="button"
                    onClick={() => handleRemoveLine(line.id)}
                    disabled={disabled}
                    className="p-1.5 text-gray-400 hover:text-danger-500 rounded-md hover:bg-gray-100 transition-colors"
                    aria-label={`Remove line ${index + 1}`}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Total */}
      {lines.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Total</span>
            <span className="text-lg font-semibold text-gray-900">
              £{formatPence(totalInPence)}
            </span>
          </div>
        </div>
      )}

      {/* Helper text */}
      {lines.length > 0 && hasIncompleteLines && (
        <p className="mt-2 text-sm text-amber-600">
          Some lines are incomplete. Fill in all categories and amounts.
        </p>
      )}
    </div>
  );
};

export default BudgetLineEditor;
